import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import api from "@/lib/api";
import ProductPhoto from "@/components/site/ProductPhoto";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";
import { formatPrice, waLink, colorHex, brandButton } from "@/lib/whatsapp";

const slug = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-");

export default function Echarpes() {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    document.title = "ZULIANY — Écharpes";
    api
      .get("/products")
      .then((res) => setProducts(res.data.filter((p) => p.category === "echarpe")))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const pick = (id, name) => setSelected((s) => ({ ...s, [id]: name }));

  const wa = (p) =>
    waLink(
      `Bonjour Zuliany, je suis intéressé(e) par l'écharpe « ${p.brand} ${p.name} »${
        selected[p.id] ? ` en couleur ${selected[p.id]}` : ""
      }. Est-elle toujours disponible ?`
    );

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]">
      <header className="pt-10 text-center">
        <Link
          to="/"
          data-testid="echarpes-home-link"
          className="inline-block transition-transform duration-300 hover:scale-105"
          aria-label="Retour à l'accueil Zuliany"
        >
          <img src="/logo-light.png" alt="Zuliany" className="mx-auto h-24 w-24 object-contain" />
        </Link>
      </header>

      <main className="mx-auto max-w-4xl px-6 pb-20">
        <div className="text-center">
          <p className="eyebrow mt-2">La Sélection</p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Écharpes</h1>
          <p className="mx-auto mt-3 max-w-md text-sm font-light leading-relaxed text-zinc-600">
            Choisissez votre couleur — les teintes barrées ne sont plus
            disponibles.
          </p>
        </div>

        {loaded && products.length === 0 ? (
          <div className="mx-auto mt-10 max-w-md border border-dashed border-zinc-300 px-6 py-14 text-center">
            <p className="font-serif text-2xl">La sélection se renouvelle</p>
            <p className="mt-2 text-sm font-light text-zinc-500">
              De nouvelles écharpes arrivent très bientôt.
            </p>
            <a
              data-testid="echarpes-empty-whatsapp-button"
              href={waLink("Bonjour Zuliany, quand arrivent les nouvelles écharpes ?")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-gold-gradient px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-black"
            >
              <WhatsAppIcon className="h-3.5 w-3.5" />
              Me tenir informé(e)
            </a>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {products.map((p) => {
              const bs = brandButton(p.brand);
              const sel = p.colors && p.colors.find((c) => c.name === selected[p.id]);
              return (
                <article
                  key={p.id}
                  data-testid={`echarpe-card-${p.id}`}
                  className="flex h-full flex-col border border-zinc-200 bg-white"
                >
                  <div className="overflow-hidden">
                    <ProductPhoto
                      key={(sel && sel.name) || "main"}
                      photos={
                        sel && (sel.photo || (sel.photos && sel.photos.length))
                          ? [sel.photo, ...(sel.photos || [])]
                          : [p.image_url, ...(p.images || [])].filter(Boolean).length
                            ? [p.image_url, ...(p.images || [])]
                            : [(p.colors || []).map((c) => c.photo).find(Boolean)].filter(Boolean)
                      }
                      alt={`${p.brand} ${p.name}${sel ? ` — ${sel.name}` : ""}`}
                      className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
                      testidPrefix="echarpe"
                      sold={p.status === "sold"}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      {p.brand}
                    </p>
                    <h2 className="mt-1 font-serif text-2xl">{p.name}</h2>
                    {p.status === "sold" && (
                      <p
                        data-testid={`soldout-echarpe-${p.id}`}
                        className="mt-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-red-500"
                      >
                        Sold Out
                      </p>
                    )}
                    <p className="mt-1 text-xs font-light text-zinc-500">{p.condition}</p>

                    {p.accessories && p.accessories.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`accessories-echarpe-${p.id}`}>
                        {p.accessories.map((a) => (
                          <span
                            key={a}
                            className="flex items-center gap-1 border border-zinc-200 bg-zinc-50 px-2 py-1 text-[10px] text-zinc-600"
                          >
                            <Check className="h-3 w-3 text-emerald-600" />
                            {a}
                          </span>
                        ))}
                      </div>
                    )}

                    {p.colors && p.colors.length > 0 && (
                      <div className="mt-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                          Couleur{selected[p.id] ? ` — ${selected[p.id]}` : ""}
                        </p>
                        <div className="mt-2.5 flex flex-wrap gap-2.5">
                          {p.colors.map((c) => (
                            <button
                              key={c.name}
                              data-testid={`color-option-${p.id}-${slug(c.name)}`}
                              disabled={!c.available}
                              onClick={() => pick(p.id, c.name)}
                              title={!c.available ? `${c.name} — indisponible` : c.name}
                              aria-label={`Couleur ${c.name}${c.available ? "" : " (indisponible)"}`}
                              style={{ backgroundColor: colorHex(c.name) }}
                              className={`relative h-9 w-9 rounded-full transition-all duration-200 ${
                                c.available ? "hover:scale-110" : "cursor-not-allowed opacity-50"
                              } ${
                                selected[p.id] === c.name
                                  ? "ring-2 ring-[#0A0A0B] ring-offset-2"
                                  : "ring-1 ring-zinc-300"
                              }`}
                            >
                              {selected[p.id] === c.name && (
                                <Check
                                  className="absolute inset-0 m-auto h-4 w-4 text-white mix-blend-difference"
                                  strokeWidth={3}
                                />
                              )}
                              {!c.available && (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <span className="block h-[2px] w-[135%] -rotate-45 bg-zinc-600" />
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                        {sel && (sel.dimension || sel.matiere) && (
                          <div
                            data-testid={`color-details-${p.id}`}
                            className="mt-3 border-t border-zinc-100 pt-3 text-xs font-light leading-relaxed text-zinc-600"
                          >
                            {p.reference && (
                              <p>
                                <span className="text-zinc-400">Référence — </span>
                                {p.reference}
                              </p>
                            )}
                            {(sel.dimension || p.taille) && (
                              <p>
                                <span className="text-zinc-400">Dimension — </span>
                                {sel.dimension || p.taille}
                              </p>
                            )}
                            {(sel.matiere || p.matiere) && (
                              <p>
                                <span className="text-zinc-400">Matière — </span>
                                {sel.matiere || p.matiere}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-auto flex items-center justify-between gap-3 border-t border-zinc-100 pt-4">
                      <div className="flex items-center gap-2">
                        <div>
                          {p.retail_price > p.price && (
                            <p className="text-xs text-zinc-400 line-through">
                              {formatPrice(p.retail_price)}
                            </p>
                          )}
                          <p className="font-sarabun text-lg">{formatPrice(p.price)}</p>
                        </div>
                        {p.retail_price > p.price && (
                          <span
                            data-testid={`discount-echarpe-${p.id}`}
                            className="border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700"
                          >
                            −{Math.round((1 - p.price / p.retail_price) * 100)} %
                          </span>
                        )}
                      </div>
                      <a
                        data-testid={`echarpe-whatsapp-${p.id}`}
                        href={wa(p)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={bs ? { backgroundColor: bs.bg } : undefined}
                        className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] transition-transform duration-300 hover:-translate-y-0.5 ${
                          bs ? "text-white" : "bg-gold-gradient text-black"
                        }`}
                      >
                        <WhatsAppIcon className="h-3.5 w-3.5" />
                        {bs ? `${bs.mono} · Contact me` : "Contact me"}
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      <footer className="pb-10 text-center">
        <Link
          data-testid="echarpes-admin-link"
          to="/admin"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 transition-colors hover:text-zinc-500"
        >
          Espace Pro
        </Link>
      </footer>
    </div>
  );
}
